import React from "react";

export const cls = (...xs) => xs.filter(Boolean).join(" ");

export const STATUS_COLORS = {
  MERGED: "bg-violet-100 text-violet-800 border-violet-300",
  FAILED_TO_APPEAR: "bg-rose-700 text-white border-rose-800 font-bold",
  PRESENT_IN_COURT: "bg-emerald-50 text-emerald-800 border-emerald-300",
  PRESENT_IN_CUSTODY: "bg-rose-50 text-rose-700 border-rose-300",
  RELEASED_PENDING_TRIAL: "bg-sky-50 text-sky-800 border-sky-300",
  IN_CUSTODY: "bg-rose-700 text-white border-rose-800 font-bold",
  RELEASED: "bg-slate-100 text-slate-600 border-slate-300",
  SET: "bg-amber-50 text-amber-800 border-amber-300",
  POSTED: "bg-emerald-50 text-emerald-800 border-emerald-300",
  FORFEITED: "bg-rose-700 text-white border-rose-800 font-bold",
  REFUNDED: "bg-slate-100 text-slate-600 border-slate-300",
  EXONERATED: "bg-slate-100 text-slate-600 border-slate-300",
  OPEN: "bg-sky-50 text-sky-800 border-sky-300",
  PENDING: "bg-amber-50 text-amber-800 border-amber-300",
  ACTIVE: "bg-emerald-50 text-emerald-800 border-emerald-300",
  STAYED: "bg-slate-100 text-slate-600 border-slate-300",
  DISPOSED: "bg-slate-100 text-slate-600 border-slate-400",
  CLOSED: "bg-slate-100 text-slate-500 border-slate-300",
  REOPENED: "bg-violet-50 text-violet-800 border-violet-300",
  SEALED: "bg-rose-700 text-white border-rose-800 font-bold",
  CONFIDENTIAL: "bg-rose-700 text-white border-rose-800 font-bold",
  SCHEDULED: "bg-sky-50 text-sky-800 border-sky-300",
  HELD: "bg-emerald-50 text-emerald-800 border-emerald-300",
  SUBMITTED: "bg-amber-50 text-amber-800 border-amber-300",
  UNDER_REVIEW: "bg-sky-50 text-sky-800 border-sky-300",
  FILED: "bg-emerald-50 text-emerald-800 border-emerald-300",
  ACCEPTED: "bg-emerald-50 text-emerald-800 border-emerald-300",
  REJECTED: "bg-rose-50 text-rose-700 border-rose-300",
  RETURNED: "bg-orange-50 text-orange-800 border-orange-300",
  STRICKEN: "bg-rose-50 text-rose-700 border-rose-300",
  COMPLETED: "bg-emerald-50 text-emerald-800 border-emerald-300",
  IN_PROGRESS: "bg-sky-50 text-sky-800 border-sky-300",
  CANCELLED: "bg-slate-100 text-slate-500 border-slate-300",
  URGENT: "bg-rose-50 text-rose-700 border-rose-300",
  HIGH: "bg-orange-50 text-orange-800 border-orange-300",
  NORMAL: "bg-slate-100 text-slate-600 border-slate-300",
  LOW: "bg-slate-50 text-slate-500 border-slate-200",
  READY: "bg-emerald-50 text-emerald-800 border-emerald-300",
  CALLED: "bg-violet-50 text-violet-800 border-violet-300",
  CONTINUED: "bg-amber-50 text-amber-800 border-amber-300",
  RESCHEDULED: "bg-amber-50 text-amber-800 border-amber-300",
  NO_APPEARANCE: "bg-rose-50 text-rose-700 border-rose-300",
  DRAFT: "bg-slate-100 text-slate-600 border-slate-300",
  PENDING_SIGNATURE: "bg-amber-50 text-amber-800 border-amber-300",
  SIGNED: "bg-sky-50 text-sky-800 border-sky-300",
  ENTERED: "bg-emerald-50 text-emerald-800 border-emerald-300",
  SUPERSEDED: "bg-slate-100 text-slate-500 border-slate-300",
  VACATED: "bg-rose-50 text-rose-700 border-rose-300",
  PROPOSED: "bg-slate-100 text-slate-600 border-slate-300",
  AUTHORIZED: "bg-sky-50 text-sky-800 border-sky-300",
  AMENDED: "bg-violet-50 text-violet-800 border-violet-300",
  DISMISSED: "bg-rose-50 text-rose-700 border-rose-300",
  CONVICTED: "bg-slate-800 text-white border-slate-800",
  ACQUITTED: "bg-emerald-50 text-emerald-800 border-emerald-300",
  PENDING_REVIEW: "bg-amber-50 text-amber-800 border-amber-300",
  CHARGED: "bg-emerald-50 text-emerald-800 border-emerald-300",
  DECLINED: "bg-rose-50 text-rose-700 border-rose-300",
};

/* ---- Enterprise design system: one dense token set shared by V2 workspaces and every legacy page ---- */

export const Badge = ({ value, testId, className }) => (
  <span data-testid={testId}
    className={cls("inline-block rounded-[2px] border px-1.5 text-[10px] font-semibold uppercase leading-[15px] tracking-tight whitespace-nowrap align-middle",
      STATUS_COLORS[value] || "bg-slate-100 text-slate-600 border-slate-300", className)}>
    {(value || "—").replace(/_/g, " ")}
  </span>
);

export const Panel = ({ title, action, children, testId, className }) => (
  <section data-testid={testId} className={cls("ent-panel", className)}>
    {(title || action) && (
      <header className="ent-section-hdr">
        {title && <span className="underline underline-offset-2 truncate">{title}</span>}
        {action && <span className="ml-auto flex items-center gap-1 font-normal shrink-0">{action}</span>}
      </header>
    )}
    {children}
  </section>
);

/** Dense page title row: 13px bold title · 11px subtitle, actions docked right. */
export const PageHeader = ({ title, subtitle, actions, testId }) => (
  <div className="ent-page-hdr" data-testid={testId}>
    <h1>{title}</h1>
    {subtitle && <p>{subtitle}</p>}
    {actions && <div className="ml-auto flex items-center gap-1 shrink-0">{actions}</div>}
  </div>
);

/** Segmented filter (status / view switcher). options: [[value, label], ...] */
export const Seg = ({ options, value, onChange, testId, className }) => (
  <div className={cls("inline-flex border border-slate-300 rounded-sm overflow-hidden text-[11px] leading-4 bg-white", className)} data-testid={testId}>
    {options.map(([v, l]) => (
      <button key={v} type="button" onClick={() => onChange(v)} data-testid={testId ? `${testId}-${String(v).toLowerCase()}` : undefined}
        className={cls("px-2 py-[2px] font-semibold uppercase tracking-tight border-r border-slate-300 last:border-r-0", value === v ? "bg-ujms-dark text-white" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900")}>
        {l}
      </button>
    ))}
  </div>
);

export const thCls = "ent-th text-left px-1.5 py-1 text-[11px] font-bold tracking-wider uppercase whitespace-nowrap border-r border-white/20 last:border-r-0 select-none";
export const tdCls = "px-1.5 py-[3px] border-b border-slate-200 text-[12px] leading-4 text-slate-800 align-top font-medium";
export const inputCls = "w-full border border-slate-300 rounded-[3px] px-1.5 py-[2px] text-[11px] leading-4 bg-white text-slate-900 transition-colors focus:outline-none focus:border-ujms focus:ring-1 focus:ring-ujms/30 placeholder:text-slate-400";
export const selectCls = inputCls;
export const btnPrimary = "inline-flex items-center gap-1 bg-gradient-to-r from-ujms-dark to-ujms text-white text-[11px] font-bold px-2.5 py-[3px] leading-4 rounded-[3px] shadow-sm hover:from-ujms-deep hover:to-ujms-dark transition-all duration-150 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap";
export const btnGhost = "inline-flex items-center gap-1 border border-slate-300 bg-white text-slate-700 text-[11px] font-semibold px-2.5 py-[3px] leading-4 rounded-[3px] hover:bg-slate-100 hover:text-slate-900 hover:border-slate-400 transition-all duration-150 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap";
export const btnDanger = "inline-flex items-center gap-1 border border-rose-300 bg-rose-50 text-rose-700 text-[11px] font-semibold px-2.5 py-[3px] leading-4 rounded-[3px] hover:bg-rose-100 hover:border-rose-400 transition-all duration-150 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap";

export const Field = ({ label, children, required }) => (
  <label className="block min-w-0">
    <span className="block text-[10px] font-semibold uppercase tracking-wide text-slate-500 leading-3 mb-px truncate">
      {label}{required && <span className="text-red-600 ml-0.5">*</span>}
    </span>
    {children}
  </label>
);

export const EmptyState = ({ icon: Icon, title, note, testId }) => (
  <div data-testid={testId} className="flex items-center gap-1.5 px-2 py-1.5 text-[11px] text-slate-400 italic">
    {Icon && <Icon className="w-3 h-3 shrink-0" />}
    <span>{title}</span>
    {note && <span className="text-slate-400/80 not-italic">— {note}</span>}
  </div>
);

/** Dense KPI tile: value and label on one line. */
export const Stat = ({ label, value, testId, onClick, tone }) => (
  <button type="button" data-testid={testId} onClick={onClick} data-pdf="stat"
    className={cls("text-left bg-white border border-slate-300 rounded-[3px] px-2 py-1 w-full flex items-baseline gap-2 leading-4 min-w-0 shadow-[0_1px_2px_rgba(15,23,42,0.05)] transition-colors",
      onClick ? "hover:bg-ujms-soft hover:border-ujms cursor-pointer" : "cursor-default")}>
    <span className={cls("text-[15px] font-bold tabular-nums shrink-0", tone || "text-slate-900")} data-pdf="stat-value">{value ?? "—"}</span>
    <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 truncate" data-pdf="stat-label">{label}</span>
  </button>
);

export const fmtDT = (iso) => (iso ? String(iso).slice(0, 16).replace("T", " ") : "—");
export const fmtD = (iso) => (iso ? String(iso).slice(0, 10) : "—");
export const ageOf = (iso) => {
  if (!iso) return "—";
  const h = Math.floor((Date.now() - new Date(iso).getTime()) / 36e5);
  return h < 24 ? `${h}h` : `${Math.floor(h / 24)}d`;
};

export const can = (access, ...perms) => perms.some((p) => access?.permissions?.includes(p));
export const JUDICIAL_ROLES = ["Judge", "Presiding Judge", "Chief Judge"];
export const isJudicial = (access) => (access?.ujms_roles || []).some((r) => JUDICIAL_ROLES.includes(r));
